'use client';

import * as React from 'react';
import {
  Plus,
  LayoutDashboard,
  CircleDot,
  CalendarRange,
  Users,
  FileText,
  FolderOpen,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useSpace } from '@/contexts/space-context';
import { useProject } from '@/contexts/project-context';
import { ProjectSelector } from '@/components/project-selector';
import { CreateProjectModal } from '@/components/create-project-modal';
import { CreateIssueDialog } from '@/components/create-issue-dialog';
import { useSidebar } from '@/components/ui/sidebar';
import { usePathname, useRouter } from '@/compat/navigation';
import { cn } from '@/lib/utils';
import {
  SIDEBAR_HEADER_CLASSES,
  SIDEBAR_NAV_CONTAINER_CLASSES,
  SIDEBAR_NAV_ITEM_CLASSES,
  SIDEBAR_NAV_ITEM_ACTIVE_CLASSES,
  SIDEBAR_NAV_ITEM_INACTIVE_CLASSES,
} from '@/lib/sidebar-styles';

const projectNavItems = [
  { icon: LayoutDashboard, label: 'Overview', path: 'overview' },
  { icon: CircleDot, label: 'Issues', path: 'issues' },
  { icon: CalendarRange, label: 'Timeline', path: 'timeline' },
  { icon: Users, label: 'Teams', path: 'teams' },
  { icon: FileText, label: 'Pages', path: 'pages' },
];

export function NavSidebarContent() {
  const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false);
  const [isCreateIssueOpen, setIsCreateIssueOpen] = React.useState(false);
  const { space } = useSpace();
  const { selectedProject } = useProject();
  const pathname = usePathname();
  const router = useRouter();
  const { setOpenMobile } = useSidebar();

  const getProjectHref = (path: string) => {
    if (!selectedProject) return '#';
    return `/spaces/${space.slug}/projects/${selectedProject.code}/${path}`;
  };

  const isActive = (path: string) => {
    return pathname.includes(`/${path}`);
  };

  const handleNavigate = (href: string) => {
    router.push(href);
    setOpenMobile(false);
  };

  return (
    <>
      <div className={cn(SIDEBAR_HEADER_CLASSES, 'justify-between gap-1')}>
        <div className="flex-1 min-w-0">
          <ProjectSelector onCreateClick={() => setIsCreateModalOpen(true)} />
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={() => setIsCreateModalOpen(true)}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      <ScrollArea className="flex-1">
        {selectedProject ? (
          <>
            <div className={SIDEBAR_NAV_CONTAINER_CLASSES}>
              <button
                onClick={() => setIsCreateIssueOpen(true)}
                className={cn(SIDEBAR_NAV_ITEM_CLASSES, 'text-primary font-medium')}
              >
                <Plus className="h-4 w-4" />
                <span>New Issue</span>
              </button>
            </div>

            <div className={SIDEBAR_NAV_CONTAINER_CLASSES}>
              {projectNavItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => handleNavigate(getProjectHref(item.path))}
                  className={cn(
                    SIDEBAR_NAV_ITEM_CLASSES,
                    isActive(item.path) ? SIDEBAR_NAV_ITEM_ACTIVE_CLASSES : SIDEBAR_NAV_ITEM_INACTIVE_CLASSES
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center px-3 py-8 text-center">
            <FolderOpen className="w-12 h-12 text-muted-foreground/50 mb-4" />
            <h3 className="font-medium text-sm mb-1">No project selected</h3>
            <p className="text-xs text-muted-foreground">
              Select or create a project to get started
            </p>
          </div>
        )}
      </ScrollArea>

      <CreateProjectModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />

      <CreateIssueDialog
        isOpen={isCreateIssueOpen}
        onClose={() => setIsCreateIssueOpen(false)}
      />
    </>
  );
}
